import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, useAnimation, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import MagneticButton from '../components/MagneticButton';

const Hero = () => {
  const controls = useAnimation();
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: false
  });

  useEffect(() => {
    if (inView) {
      controls.start("visible");
    }
  }, [controls, inView]);

  // Enhanced animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.3
      }
    }
  };

  const wordVariants = {
    hidden: { 
      y: 80,
      opacity: 0,
      rotate: -5
    },
    visible: {
      y: 0,
      opacity: 1,
      rotate: 0,
      transition: {
        type: "spring",
        damping: 15,
        stiffness: 100,
        mass: 0.5
      }
    }
  };

  const lineVariants = {
    hidden: { 
      width: 0,
      opacity: 0 
    },
    visible: {
      width: "100%",
      opacity: 1,
      transition: {
        duration: 1.5,
        ease: [0.19, 1, 0.22, 1],
        delay: 0.4
      }
    }
  };

  const descriptionVariants = {
    hidden: { 
      opacity: 0,
      x: -30
    },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.8,
        ease: "easeOut",
        delay: 0.8
      }
    }
  };

  const letterVariants = {
    hidden: { 
      opacity: 0,
      y: 50
    },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: i * 0.05,
        type: "spring",
        stiffness: 150
      }
    })
  };

  // Split text into letters for animation
  const splitLetters = (text) => {
    return text.split("").map((letter, i) => (
      <motion.span 
        key={i} 
        custom={i}
        variants={letterVariants}
      >
        {letter === " " ? "\u00A0" : letter}
      </motion.span>
    ));
  };

  return (
    <section className="hero-section" id="home" ref={ref}>
      <motion.div 
        className="hero-container"
        initial="hidden"
        animate={controls}
        variants={containerVariants}
      >
        <div className="hero-content">
          {/* Studio name/tagline - animated */}
          <motion.div 
            className="studio-tagline"
            initial={{ opacity: 0, y: -20 }}
            animate={{ 
              opacity: 1, 
              y: 0,
              transition: { delay: 0.2 }
            }}
          >
           
          </motion.div>

          <div className="heading-row first-row">
            <motion.h1 
              className="hero-heading"
              variants={wordVariants}
              custom={0}
            >
              {splitLetters("Full")}
            </motion.h1>
            
            <motion.div 
              className="animated-line"
              variants={lineVariants}
            />
            
            <motion.h1 
              className="hero-heading"
              variants={wordVariants}
              custom={0.2}
            >
              {splitLetters("Stack")}
            </motion.h1>
          </div>

          <div className="heading-row second-row">
            <motion.h1 
              className="hero-heading"
              variants={wordVariants}
              custom={0.4}
            >
              {splitLetters("Development")}
            </motion.h1>
            
            <motion.div 
              className="hero-description-container"
              variants={descriptionVariants}
            >
              <p className="hero-description">
                <motion.span
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 1.2 }}
                >
                  Professional digital solutions with cutting-edge technology. We specialize in React, Node.js, and Python development for enterprise-grade applications.
                </motion.span>
              </p>
              
              <MagneticButton>
  <Link to="/work" className="portfolio-link">
    <span className="button-text">
      View Our Work </span>
      
    <div className="arrow-icon">
      <svg viewBox="0 0 24 24" fill="none">
        <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" strokeWidth="2"/>
      </svg>
    </div>
  </Link>
</MagneticButton>
            </motion.div>
          </div>
        </div>

        <motion.div 
          className="scroll-indicator"
          initial={{ opacity: 0, y: 20 }}
          animate={{ 
            opacity: [0, 1, 0.6, 1], 
            y: 0,
            transition: { 
              delay: 1.8,
              duration: 2,
              repeat: Infinity,
              repeatType: "reverse"
            }
          }}
        >
          <motion.div 
            className="scroll-line"
            animate={{
              y: [0, 15, 0],
              transition: {
                duration: 2,
                repeat: Infinity,
                ease: "easeInOut"
              }
            }}
          />
          <motion.span
            animate={{
              opacity: [0.6, 1, 0.6],
              transition: {
                duration: 2,
                repeat: Infinity
              }
            }}
          >
            Scroll
          </motion.span>
        </motion.div>

        {/* Background decorative elements */}
        <motion.div 
          className="hero-bg-circle large"
          initial={{ scale: 0, opacity: 0 }}
          animate={{ 
            scale: 1, 
            opacity: 0.1,
            transition: { delay: 0.5, duration: 1.5 }
          }}
        />
        <motion.div 
          className="hero-bg-circle medium"
          initial={{ scale: 0, opacity: 0 }}
          animate={{ 
            scale: 1, 
            opacity: 0.15,
            transition: { delay: 0.7, duration: 1.5 }
          }}
        />
      </motion.div>
    </section>
  );
};

export default Hero;